import { useMemo, useState } from 'react';

import {
  formatCrossChainDuration,
  getCrossChainEstimateSeconds,
} from '@/lib/api/0x-cross-chain/status';
import { tokenFormatter } from '@/lib/formatting';

import {
  ToastContainer,
  ToastContent,
  ToastTitle,
  useAutoDismiss,
} from '@/components/TransactionToast/ToastComponents';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import type { CrossChainTransaction } from '@/store/transaction/types';
import { isTransactionPending, isTransactionSuccessful } from '@/store/transaction/utils';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';

import { FeedbackAnimation } from '../animations/FeedbackAnimation';
import { TokenSwapAnimation } from '../animations/TokenSwapAnimation';
import { TradeToastDetailsLink } from './TradeToastDetailsLink';

type TradeToastCrossChainProps = {
  transaction: CrossChainTransaction;
  onDismiss: () => void;
  onHeightChange: () => void;
};

type CrossChainTransactionStatus =
  | 'pending'
  | 'success'
  | 'error'
  | 'refunding'
  | 'refunded'
  | 'actionRequired';

const statusTitleMap: Record<CrossChainTransactionStatus, string> = {
  pending: 'Cross-chain swap in progress...',
  success: 'Transaction successful',
  error: 'Transaction failed',
  refunding: 'Swap failed - refund in progress',
  refunded: 'Refund complete',
  actionRequired: 'Action needed to recover funds',
};

export const TradeToastCrossChain = ({
  transaction,
  onDismiss,
  onHeightChange,
}: TradeToastCrossChainProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const {
    sellToken,
    buyToken,
    buyAmount,
    sellAmount,
    hash,
    sourceTxHash,
    serviceTime,
    failureContext,
  } = transaction;

  const isSuccess = isTransactionSuccessful(transaction);
  const refundStatus = failureContext?.status;

  const status: CrossChainTransactionStatus = useMemo(() => {
    if (isTransactionPending(transaction)) {
      return refundStatus === 'refund_pending' ? 'refunding' : 'pending';
    } else if (isSuccess) {
      return 'success';
    } else if (refundStatus === 'refund_succeeded') {
      return 'refunded';
    } else if (refundStatus === 'manual_action_required') {
      return 'actionRequired';
    }
    return 'error';
  }, [isSuccess, refundStatus, transaction]);

  const isPending = status === 'pending' || status === 'refunding';
  const feedback =
    status === 'pending'
      ? undefined
      : status === 'success' || status === 'refunded'
        ? 'success'
        : 'error';

  useAutoDismiss(isHovered, onDismiss, isPending, true);

  const buyAmountFormatted = tokenFormatter.format(buyAmount, { decimals: buyToken.decimals });
  const sellAmountFormatted = tokenFormatter.format(sellAmount, { decimals: sellToken.decimals });

  const showDetailsLink = ['success', 'error', 'refunded', 'actionRequired'].includes(status);
  const detailsLabel = status === 'actionRequired' ? 'Recover funds' : 'View trade detail';

  const title =
    status === 'error' && failureContext?.reason ? failureContext.reason : statusTitleMap[status];

  return (
    <ToastContainer
      onClose={onDismiss}
      onHeightChange={onHeightChange}
      showProgressAnimation={isPending}
      onHoverChange={setIsHovered}
    >
      <FeedbackAnimation feedback={feedback} className="relative z-10 shrink-0">
        <TokenSwapAnimation fromToken={sellToken} toToken={buyToken} isComplete={!isPending} />
      </FeedbackAnimation>
      <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-1">
        <ToastTitle>{title}</ToastTitle>
        <ToastContent>
          {status === 'pending' ? (
            <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-1600' })}>
              Estimated time:{' '}
              {formatCrossChainDuration(
                getCrossChainEstimateSeconds(sellToken.chainId, serviceTime),
              )}
            </span>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center whitespace-nowrap">
                <span
                  className={fontStyles({
                    variant: 'LABEL_MD',
                    class: 'text-token-gray-1600 min-w-0 block truncate',
                  })}
                >
                  {sellAmountFormatted} {sellToken.symbol}
                </span>
                <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-700' })}>
                  &nbsp;on&nbsp;
                </span>
                <div className="flex items-center shrink-0">
                  <ChainAvatar
                    chainId={sellToken.chainId}
                    alt={NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]}
                    size={16}
                    className="mr-1"
                  />
                  <span
                    className={fontStyles({
                      variant: 'LABEL_MD',
                      class: 'text-token-gray-700 whitespace-nowrap',
                    })}
                  >
                    {NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]}
                  </span>
                  <span
                    className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-1600' })}
                  >
                    &nbsp;to
                  </span>
                </div>
              </div>
              <div className="flex items-center whitespace-nowrap">
                <span
                  className={fontStyles({
                    variant: 'LABEL_MD',
                    class: 'text-token-gray-1600 min-w-0 block truncate',
                  })}
                >
                  {buyAmountFormatted} {buyToken.symbol}
                </span>
                <span className={fontStyles({ variant: 'LABEL_MD', class: 'text-token-gray-700' })}>
                  &nbsp;on&nbsp;
                </span>
                <div className="flex items-center shrink-0">
                  <ChainAvatar
                    chainId={buyToken.chainId}
                    alt={NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]}
                    size={16}
                    className="mr-1"
                  />
                  <span
                    className={fontStyles({
                      variant: 'LABEL_MD',
                      class: 'text-token-gray-700 whitespace-nowrap',
                    })}
                  >
                    {NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]}
                  </span>
                </div>
              </div>
            </div>
          )}
        </ToastContent>
        {showDetailsLink && (
          <div className="mt-1">
            <TradeToastDetailsLink
              chainId={sellToken.chainId}
              hash={sourceTxHash ?? hash}
              onDismiss={onDismiss}
              label={detailsLabel}
            />
          </div>
        )}
      </div>
    </ToastContainer>
  );
};
